import PropTypes from 'prop-types';
import { Children, cloneElement, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import ImmutablePropTypes from 'react-immutable-proptypes';
import ImmutablePureComponent from 'react-immutable-pure-component';

import { StatusSkeletonLoading } from 'mastodon/components/status-skeleton-loading';
import { AccountHeader } from 'mastodon/features/account_timeline/components/account_header';
import { Card } from 'mastodon/features/explore/components/card';
import { identityContextPropShape } from 'mastodon/identity_context';
import { me } from 'mastodon/initial_state';

import { scrollRight } from '../../../scroll';
import ComposeFormTrigger from '../../compose/components/compose_form_trigger';
import BundleContainer from '../containers/bundle_container';
import {
  Compose,
  Notifications,
  HomeTimeline,
  CommunityTimeline,
  PublicTimeline,
  HashtagTimeline,
  DirectTimeline,
  FavouritedStatuses,
  BookmarkedStatuses,
  ListTimeline,
  Directory,
} from '../util/async-components';
import { useColumnsContext } from '../util/columns_context';

import BundleColumnError from './bundle_column_error';
import { ColumnLoading } from './column_loading';
import ComposePanel from './compose_panel';
import DrawerLoading from './drawer_loading';
import NavigationPanel from './navigation_panel';

const componentMap = {
  'COMPOSE': Compose,
  'HOME': HomeTimeline,
  'NOTIFICATIONS': Notifications,
  'PUBLIC': PublicTimeline,
  'REMOTE': PublicTimeline,
  'COMMUNITY': CommunityTimeline,
  'HASHTAG': HashtagTimeline,
  'DIRECT': DirectTimeline,
  'FAVOURITES': FavouritedStatuses,
  'BOOKMARKS': BookmarkedStatuses,
  'LIST': ListTimeline,
  'DIRECTORY': Directory,
};

const TabsBarPortal = () => {
  const { setTabsBarElement } = useColumnsContext();

  const setRef = useCallback((element) => {
    if (element)
      setTabsBarElement(element);
  }, [setTabsBarElement]);

  return <div id='tabs-bar__portal' ref={setRef} />;
};

class ColumnsArea extends ImmutablePureComponent {

  static propTypes = {
    identity: identityContextPropShape,
    columns: ImmutablePropTypes.list.isRequired,
    isModalOpen: PropTypes.bool.isRequired,
    singleColumn: PropTypes.bool,
    children: PropTypes.node,
    isLoading: PropTypes.bool,
    suggestions: PropTypes.array,
    location: PropTypes.object,
  };

  mediaQuery = 'matchMedia' in window && window.matchMedia('(max-width: 1174px)');

  state = {
    renderComposePanel: !(this.mediaQuery && this.mediaQuery.matches),
    accountId: null,
  };

  componentDidMount() {
    if (this.mediaQuery) {
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.handleLayoutChange);
      } else {
        this.mediaQuery.addListener(this.handleLayoutChange);
      }
      this.setState({ renderComposePanel: !this.mediaQuery.matches });
    }

    this.isRtlLayout = document.getElementsByTagName('body')[0].classList.contains('rtl');
  }

  componentWillUnmount() {
    if (this.mediaQuery) {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.handleLayoutChange);
      } else {
        this.mediaQuery.removeListener(this.handleLayoutChange);
      }
    }
  }

  handleChildrenContentChange() {
    if (!this.props.singleColumn) {
      const modifier = this.isRtlLayout ? -1 : 1;
      scrollRight(this.node, (this.node.scrollWidth - window.innerWidth) * modifier);
    }
  }

  handleLayoutChange = (e) => {
    this.setState({ renderComposePanel: !e.matches });
  };

  setRef = (node) => {
    this.node = node;
  };

  isShowForm = () => {
    if (me) {
      const path = window.location.pathname;
      return path.startsWith('/public');
    } else {
      return false;
    }
  };

  // getAccountIdFromUsername = (username) => {
  //   if (!username) return null;
  //   console.log(this.props.me);
  //   console.log(this.props.userId);

  //   // Otherwise fetch from API synchronously
  //   try {
  //     const xhr = new XMLHttpRequest();
  //     xhr.open('GET', `/api/v1/accounts/lookup?acct=${username}`, false);
  //     xhr.send();
  //     if (xhr.status === 200) {
  //       const data = JSON.parse(xhr.responseText);
  //       if (data && data.id) {
  //         return data.id;
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error fetching user info:', error);
  //   }
  //   return null;
  // };

  getAccountFromPath = () => {
    const path = window.location.pathname;
    const match = path.match(/\/@([^/]+)/);
    if (match && this.props.userId) {
      return this.props.userId;
    }
    return me;
  };

  isShowProfile = () => {
    const path = window.location.pathname;
    const match = path.match(/\/@([^/]+)/);
    const cleanPath = path.replace(/\/$/, '');
    const truePath = /^\/@[^/]+$/.test(cleanPath);
    if (truePath) {
      return match ? !!this.props.userId : false;
    }
    return false;
  };

  isShowFormPublish = () => {
    const path = window.location.pathname;
    const match = path.match(/\/@([^/]+)/);
    const cleanPath = path.replace(/\/$/, '');
    const truePath = /^\/@[^/]+$/.test(cleanPath);
    if (match && truePath) {
      return me === this.props.userId;
    }
    return path.startsWith('/publish');
  };

  renderLoading = columnId => () => {
    return columnId === 'COMPOSE' ? <DrawerLoading /> : <ColumnLoading multiColumn />;
  };

  renderError = (props) => {
    return <BundleColumnError multiColumn errorType='network' {...props} />;
  };


  isShowBackIcon = () => {
    const path = window.location.pathname;
    return path.startsWith("/publish");
  };
  isValidCustomURL() {
    const pathname = window.location.pathname;
    const regex = /^\/@[^/]+\/[^/]+$/;

    return regex.test(pathname);
  }

  handleNavigationPanelClick = (e) => {
    e.stopPropagation();
  };

  handleGoBack = () => {
    window.history.back();
  };

  render() {
    const { columns, children, singleColumn, isModalOpen, isLoading, suggestions } = this.props;
    const { renderComposePanel } = this.state;

    const specialPage = window.location.pathname?.startsWith('/@');

    if (singleColumn) {
      return (
        <div className={`columns-area__panels ${specialPage && 'columns-area__panels__special'}`}>

          <div className='columns-area__panels__pane columns-area__panels__pane--start columns-area__panels__pane--navigational'>
            <div role='presentation' onClick={this.handleNavigationPanelClick} className='columns-area__panels__pane__inner'>
              <NavigationPanel />
            </div>
          </div>


          <div className='columns-area__panels__main'>
            <div className='tabs-bar__wrapper'><TabsBarPortal /></div>
            {(this.isShowForm()) && (<div className='columns-area_panels_form__main'>
              <div>
                {this.isShowBackIcon() && <div role='presentation' onClick={this.handleGoBack} style={{ background: 'var(--background-color)', border: 'none', padding: '0 0 25px 0', textDecoration: 'none' }} className='column-header__back-button'>
                  <svg xmlns='http://www.w3.org/2000/svg' height='24' viewBox='0 -960 960 960' width='24' className='icon icon-chevron-left column-back-button__icon' aria-hidden='true'><path d='m313-440 224 224-57 56-320-320 320-320 57 56-224 224h487v80H313Z' /></svg>
                  <FormattedMessage id='column_back_button.label' defaultMessage='Back' />
                </div>}
                <div className='columns-area_panels_form_compose'>
                  <ComposeFormTrigger />
                </div>
              </div>
            </div>
            )}
            {
              this.isShowProfile() && (
                <AccountHeader accountId={this.getAccountFromPath()} />
              )
            }
            {
              this.isShowFormPublish() && (
                <div className='columns-area_panels_form_compose'>
                  <ComposeFormTrigger />
                </div>
              )
            }
            <div className='columns-area columns-area--mobile'>{children}</div>
          </div>
          <div className='columns-area__panels__pane columns-area__panels__pane--compositional columns-area__panels__pane--compositional_end' >
            <div className='columns-area__panels__pane__inner'>
              {renderComposePanel && <ComposePanel />}

              {me && <div className='columns-area__panels__pane__suggestion'>
                <h3>
                  <FormattedMessage
                    id='follow_suggestions.who_to_follow'
                    defaultMessage='Who to follow'
                  />
                </h3>
                {isLoading ? <StatusSkeletonLoading isFollowPage count={4} /> : suggestions.slice(0, 20).map(suggestion => (
                  <Card
                    key={suggestion.account_id}
                    id={suggestion.account_id}
                    source={suggestion.sources[0]}
                  />
                ))}
              </div>}

            </div>

          </div>

        </div>
      );
    }

    return (
      <div className={`columns-area ${isModalOpen ? 'unscrollable' : ''}`} ref={this.setRef}>
        {columns.map(column => {
          const params = column.get('params', null) === null ? null : column.get('params').toJS();
          const other = params && params.other ? params.other : {};

          return (
            <BundleContainer key={column.get('uuid')} fetchComponent={componentMap[column.get('id')]} loading={this.renderLoading(column.get('id'))} error={this.renderError}>
              {SpecificComponent => <SpecificComponent columnId={column.get('uuid')} params={params} multiColumn {...other} />}
            </BundleContainer>
          );
        })}

        {Children.map(children, child => cloneElement(child, { multiColumn: true }))}
      </div>
    );
  }

}

export default ColumnsArea;
